// Small building blocks of the Competencia pages.
import { Pencil, Plus, Trash2, type LucideIcon } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState, type CSSProperties, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { TrendRelevance } from "./types";
import { relevanceLabelKey } from "./intel";

/** A glass card with a titled header (icon, count, action) and its content. */
export const SectionCard = ({
  id,
  icon: Icon,
  title,
  count,
  action,
  children,
  className,
  style,
}: {
  id: string;
  icon: LucideIcon;
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) => (
  <section
    id={id}
    aria-labelledby={`${id}-title`}
    className={cn("mi-card mi-rise min-w-0 scroll-mt-36 p-4 md:p-5", className)}
    style={style}
  >
    <header className="mb-3 flex min-h-9 items-center gap-2">
      <span
        aria-hidden
        className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20"
      >
        <Icon className="size-4" />
      </span>
      <h2
        id={`${id}-title`}
        className="min-w-0 flex-1 truncate text-[17px] font-semibold tracking-tight"
      >
        {title}
        {count != null ? (
          <span className="ml-2 rounded-full bg-muted px-2 py-0.5 align-middle text-xs font-semibold text-muted-foreground tabular-nums">
            {count}
          </span>
        ) : null}
      </h2>
      {action}
    </header>
    {children}
  </section>
);

/** Round "+" in a section header (label shown from `sm` up). */
export const AddButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant="ghost"
    onClick={onClick}
    aria-label={label}
    className="h-9 shrink-0 rounded-full bg-primary/8 px-2.5 text-sm font-semibold text-primary hover:bg-primary/12 sm:px-3.5 dark:bg-primary/15"
  >
    <Plus aria-hidden className="size-4" />
    <span className="max-sm:sr-only">{label}</span>
  </Button>
);

/** A 36px round icon button (edit, history…) with a tooltip-like title. */
export const IconAction = ({
  label,
  icon: Icon = Pencil,
  onClick,
  className,
}: {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  className?: string;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={cn(
      "size-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground",
      className,
    )}
  >
    <Icon aria-hidden className="size-4" />
  </Button>
);

/** Delete in two taps: the first one asks "¿Eliminar?". */
export const ConfirmDeleteButton = ({
  label,
  onConfirm,
  disabled,
  className,
}: {
  label: string;
  onConfirm: () => void;
  disabled?: boolean;
  className?: string;
}) => {
  const translate = useTranslate();
  const [confirming, setConfirming] = useState(false);
  const confirmLabel = translate("competition.form.delete_confirm");
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={() => (confirming ? onConfirm() : setConfirming(true))}
      onBlur={() => setConfirming(false)}
      aria-label={confirming ? confirmLabel : label}
      title={label}
      className={cn(
        "h-9 min-w-9 shrink-0 rounded-full px-2.5 text-muted-foreground",
        confirming &&
          "bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive",
        className,
      )}
    >
      <Trash2 aria-hidden className="size-4" />
      {confirming ? <span className="text-xs">{confirmLabel}</span> : null}
    </Button>
  );
};

const RELEVANCE_CLASS: Record<TrendRelevance, string> = {
  alta: "bg-red-500/12 text-red-700 ring-red-500/25 dark:bg-red-400/15 dark:text-red-300 dark:ring-red-400/30",
  media:
    "bg-amber-500/14 text-amber-800 ring-amber-500/25 dark:bg-amber-400/15 dark:text-amber-200 dark:ring-amber-400/30",
  baja: "bg-slate-500/10 text-slate-700 ring-slate-500/20 dark:bg-slate-300/12 dark:text-slate-300 dark:ring-slate-300/25",
};

export const RelevanceBadge = ({
  relevance,
}: {
  relevance: TrendRelevance;
}) => {
  const translate = useTranslate();
  const known = RELEVANCE_CLASS[relevance] ? relevance : "media";
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-semibold ring-1 ring-inset",
        RELEVANCE_CLASS[known],
      )}
    >
      <span className="sr-only">
        {translate("competition.trends.relevance.label")}:{" "}
      </span>
      {translate(relevanceLabelKey(known))}
    </span>
  );
};

/** Neutral small pill (activity kind, period…). */
export const SoftChip = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex h-6 max-w-full min-w-0 items-center rounded-full bg-muted px-2.5 text-xs font-semibold text-muted-foreground">
    <span className="truncate">{children}</span>
  </span>
);

/** Cancel (desktop) + Save footer of a Competencia form dialog. */
export const FormActions = ({
  formId,
  disabled,
  onCancel,
}: {
  formId: string;
  disabled: boolean;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="rounded-full max-md:hidden"
        onClick={onCancel}
      >
        {translate("competition.form.cancel")}
      </Button>
      <Button
        type="submit"
        form={formId}
        disabled={disabled}
        className="btn-save rounded-full px-5 max-md:h-12 max-md:flex-1"
      >
        {translate("competition.form.save")}
      </Button>
    </>
  );
};
